import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Paper
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';
import AddIcon from '@mui/icons-material/Add';
import CheckboxIcon from '@mui/icons-material/CheckBox';
import CheckboxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import cardTokenService, { CardEditToken } from '../services/cardTokenService';
import axios from '../utils/axiosConfig';

interface Athlete {
  _id: string;
  firstname: string;
  surname: string;
  extraCard: boolean;
  extraCardCompetitionGroup: string;
}

interface CardTokensManagerProps {
  competitionId: string;
  clubId: string;
}

const formatExpiry = (iso: string) =>
  new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

const buildLink = (token: string) =>
  `${window.location.origin}/card-edit/${token}`;

const CardTokensManager: React.FC<CardTokensManagerProps> = ({ competitionId, clubId }) => {
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [tokens, setTokens] = useState<CardEditToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const [selectedAthletes, setSelectedAthletes] = useState<string[]>([]);
  const [label, setLabel] = useState('');
  const [durationDays, setDurationDays] = useState(3);
  const [secondCardOnly, setSecondCardOnly] = useState(false);
  const [creating, setCreating] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tokensData, athletesRes] = await Promise.all([
        cardTokenService.getTokens(competitionId, clubId),
        axios.get(`/api/card-tokens/competition/${competitionId}/athletes`)
      ]);
      setTokens(tokensData);
      setAthletes(athletesRes.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [competitionId, clubId]);

  useEffect(() => {
    if (panelOpen) loadData();
  }, [panelOpen, loadData]);

  const handleCreate = async () => {
    if (selectedAthletes.length === 0) return;
    setCreating(true);
    try {
      const token = await cardTokenService.createToken({
        competitionId,
        clubId,
        athleteIds: selectedAthletes,
        label,
        durationDays,
        secondCardOnly
      });
      const link = buildLink(token.token);
      await navigator.clipboard.writeText(link);
      setSnackbar({ open: true, message: 'Link erstellt und kopiert!', severity: 'success' });
      setCreateOpen(false);
      setSelectedAthletes([]);
      setLabel('');
      setDurationDays(3);
      setSecondCardOnly(false);
      loadData();
    } catch {
      setSnackbar({ open: true, message: 'Fehler beim Erstellen des Links.', severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (tokenId: string) => {
    try {
      await cardTokenService.revokeToken(tokenId);
      setSnackbar({ open: true, message: 'Link widerrufen.', severity: 'success' });
      loadData();
    } catch {
      setSnackbar({ open: true, message: 'Fehler beim Widerrufen.', severity: 'error' });
    }
  };

  const handleCopy = async (token: string) => {
    await navigator.clipboard.writeText(buildLink(token));
    setSnackbar({ open: true, message: 'Link kopiert!', severity: 'success' });
  };

  const toggleAthlete = (id: string) => {
    setSelectedAthletes(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const activeTokens = tokens.filter(t => !t.isRevoked && new Date(t.expiresAt) > new Date());

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<LinkIcon />}
        onClick={() => setPanelOpen(true)}
        size="small"
      >
        Bearbeitungslinks ({activeTokens.length})
      </Button>

      {/* Panel: Übersicht aller Tokens */}
      <Dialog open={panelOpen} onClose={() => setPanelOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Bearbeitungslinks für Wettkampfkarten
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              size="small"
            >
              Neuer Link
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : tokens.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              Noch keine Links erstellt.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Bezeichnung</TableCell>
                    <TableCell>Sportler</TableCell>
                    <TableCell>Gültig bis</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tokens.map(t => {
                    const expired = new Date(t.expiresAt) < new Date();
                    const isActive = !t.isRevoked && !expired;
                    return (
                      <TableRow key={t._id}>
                        <TableCell>{t.label || '—'}</TableCell>
                        <TableCell>
                          {t.athletes.map(a => `${a.firstname} ${a.surname}`).join(', ')}
                        </TableCell>
                        <TableCell>{formatExpiry(t.expiresAt)}</TableCell>
                        <TableCell>
                          {t.isRevoked ? (
                            <Chip label="Widerrufen" color="error" size="small" />
                          ) : expired ? (
                            <Chip label="Abgelaufen" size="small" />
                          ) : (
                            <Chip label="Aktiv" color="success" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {isActive && (
                            <>
                              <Tooltip title="Link kopieren">
                                <IconButton size="small" onClick={() => handleCopy(t.token)}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Link widerrufen">
                                <IconButton size="small" color="error" onClick={() => handleRevoke(t._id)}>
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPanelOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Neuen Link erstellen */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neuen Bearbeitungslink erstellen</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Bezeichnung (optional)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            size="small"
            fullWidth
            placeholder="z.B. Karten für Trainer Müller"
          />

          <FormControl size="small" fullWidth>
            <InputLabel>Gültigkeitsdauer</InputLabel>
            <Select
              value={durationDays}
              label="Gültigkeitsdauer"
              onChange={e => setDurationDays(Number(e.target.value))}
            >
              <MenuItem value={1}>1 Tag</MenuItem>
              <MenuItem value={2}>2 Tage</MenuItem>
              <MenuItem value={3}>3 Tage</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={secondCardOnly}
                onChange={e => setSecondCardOnly(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Nur 2. Wettkampfkarte zur Bearbeitung freigeben
              </Typography>
            }
          />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2">Sportler auswählen</Typography>
            {athletes.some(a => a.extraCard) && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSelectedAthletes(athletes.filter(a => a.extraCard).map(a => a._id));
                  setSecondCardOnly(true);
                }}
              >
                Nur 2. Karte
              </Button>
            )}
          </Box>
          <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto' }}>
            {athletes.map(a => {
              const selected = selectedAthletes.includes(a._id);
              return (
                <Box
                  key={a._id}
                  onClick={() => toggleAthlete(a._id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 2, py: 1, cursor: 'pointer',
                    bgcolor: selected ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  {selected ? <CheckboxIcon color="primary" fontSize="small" /> : <CheckboxOutlineBlankIcon fontSize="small" />}
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>{a.firstname} {a.surname}</Typography>
                  {a.extraCard && (
                    <Chip label="2. Karte" size="small" color="secondary" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                  )}
                </Box>
              );
            })}
          </Paper>
          <Typography variant="caption" color="text.secondary">
            {selectedAthletes.length} Sportler ausgewählt
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={selectedAthletes.length === 0 || creating}
            startIcon={creating ? <CircularProgress size={16} /> : <ContentCopyIcon />}
          >
            Link erstellen & kopieren
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CardTokensManager;
